import DOMPurify from 'dompurify';

/**
 * Convenience object for namespace TypeScript
 */
export const StringUtils = {
  equalsAny,
  equals,
  startsWith,
  decodeHTML,
  shortenString,
  sanitizeHtml,
  escapeRegExp,
};

// TODO as many of these as we actually need.
// Some of the ones we had could be replaced with newer language features, or saved very little, though.

export function equalsAny(str: string, samples: string[], caseInsensitive = false) {
  return (samples || []).some(item => equals(item, str, !caseInsensitive));
}

export function equals(val1: string, val2: string, caseSensitive = false) {
  if (caseSensitive) {
    return val1 === val2;
  } else {
    return (val1 || '').toUpperCase() === (val2 || '').toUpperCase();
  }
}

export function stringContains(value: string, searchString: string, caseInsensitive = false): boolean {
  if (!value) {
    return false;
  }

  if (caseInsensitive) {
    value = value.toUpperCase();
    searchString = searchString.toUpperCase();
  }

  return value.includes(searchString);
}

export function startsWith(str: string, suffix: string, caseSensitive: boolean = true): boolean {
  // if case sensitive if false, convert str and suffix to uppercase
  if (!caseSensitive) {
    str = str.toUpperCase();
    suffix = suffix.toUpperCase();
  }
  return str.lastIndexOf(suffix, 0) === 0;
}

export function decodeHTML(input: string) {
  //The advantage of using this code is that tags are also preserved.
  const txt = document.createElement('textarea');
  txt.innerHTML = input;
  return txt.value;
}

/**
 * Escape a string to be parsed as a regular expression, prior top parsing it.
 * @param string to escape
 */
export function escapeRegExp(string: string) {
  return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function isNullOrWhitespace(value: string | null | undefined): boolean {
  return value == null || value.trim() === '';
}

export function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function htmlToText(html: string) {
  const temp = document.createElement('div');
  temp.innerHTML = sanitizeHtml(html);
  return temp.textContent;
}

export function GetSize(byteCount: number) {
  let size: string = '0 Bytes';
  if (byteCount >= 1073741824) size = (byteCount / 1073741824).toFixed(2) + ' GB';
  else if (byteCount >= 1048576) size = (byteCount / 1048576).toFixed(2) + ' MB';
  else if (byteCount >= 1024) size = (byteCount / 1024).toFixed(2) + ' KB';
  else if (byteCount > 0 && byteCount < 1024) size = byteCount + ' Bytes';

  return size;
}

export function removeSpecialCharacters(input?: string) {
  return (input || '').replace(/[^a-zA-Z0-9]/g, '');
}

export function splitCamelCase(original: string): string {
  return (original || '').replace(/([A-Z])/g, ' $1').replace(/(^| )(\w)/g, x => x.toUpperCase());
}

/**Determines whether a string contains html reserved chars i.e. " ' & < > */
export function hasHtmlReservedCharacters(input: string) {
  if (!input) return false;
  return /["'<>&]/.test(input);
}

export function getDecodedHtmlReservedCharacters(input: string) {
  if (input && hasHtmlReservedCharacters(input)) return decodeHTML(input);
  return input;
}

/**
 * Options for how to truncate the string
 */
interface IShortenStringOptions {
  dropFrom?: 'end' | 'start' | 'middle';
  /**
   * Defaults to an ellipsis character (U+2026);
   */
  replacement?: string;
  /** `false` if the measurement of total shouldn't include the replacement string. */
  countIncludesReplacement?: boolean;
  /**
   * word separator when abbreviating. This _entails_ the desire to preserve whole words, which in turn means the
   * length will be taken as an approximation.
   */
  separator?: string;
  /**
   * True to keep separator in between replacement and kept words,
   */
  keepSeparator?: string;
}

export const ELLIPSIS = '…'; // (U+2026);

/**
 * Take a long string down to a given length
 * @param input string to truncate
 * @param length to limit string to. defaults to 30
 * @param {IShortenStringOptions} options optional config object
 */
export function shortenString(input: string, length = 30, options?: IShortenStringOptions) {
  if (!input || input.length <= length) {
    return input || '';
  }

  const { dropFrom = 'end', replacement = ELLIPSIS, countIncludesReplacement = true, separator = '', keepSeparator } =
    options || {};
  const s7r = input.includes(separator) ? separator : '';
  length = length - (countIncludesReplacement ? replacement.length : 0);

  let head = input;
  let tail = '';

  // this rough 60/40 rounding may be a bit imprecise / arbitrary, but it's consistent with legacy backend code, per charts.
  // could make this "split" configurable, too - here roughly 40/60 for 'middle'
  const startPercent = 0.35;
  const endPercent = 0.6;
  let headEnd = dropFrom === 'end' ? length : dropFrom === 'start' ? 0 : Math.round(length * startPercent);
  let tailStart =
    dropFrom === 'end'
      ? input.length
      : dropFrom === 'start'
      ? input.length - length
      : input.length - Math.round(length * endPercent);

  if (s7r) {
    // looping indexOf should be faster than looping string manipulations here:
    let prevStart = input.length;
    let start = input.length;
    let i = 0; // debug
    while (start > tailStart && i < 100) {
      prevStart = start;
      start = input.lastIndexOf(s7r, prevStart - 1);
    }
    tailStart = start + 1;

    let prevEnd = 0;
    let endIndex = 0;
    let j = 0; // debug
    while (endIndex > -1 && endIndex < headEnd && j < 100) {
      endIndex = input.indexOf(s7r, prevEnd + 1);
      endIndex > -1 && (prevEnd = endIndex);
    }
    headEnd = prevEnd;
  }

  tail = input.slice(tailStart);
  head = headEnd === 0 ? '' : input.slice(0, headEnd);

  if (keepSeparator) {
    const startS7r = head.length ? s7r : '';
    const endS7r = tail.length ? s7r : '';
    return `${head}${startS7r}${replacement}${endS7r}${tail}`;
  }
  return `${head}${replacement}${tail}`;
}

export function sanitizeHtml(html: string, allowAttributes: string[] = [], forceBody: boolean = false) {
  //FORCE_BODY is used here to allow internal Style in a html.
  return DOMPurify.sanitize(html, { ADD_ATTR: allowAttributes || [], FORCE_BODY: forceBody });
}

export function encodeHTML(input: string) {
  const div = document.createElement('div');
  div.textContent = input;

  return div.outerHTML.replace(/"/g, '&quot;');
}
